import type { RequestStatus } from './enums';
import { isClosedRequestStatus } from './vehicle-requests';

/**
 * Адреса записей портала: куда ведёт номер, названный не на своём экране.
 *
 * Здесь, а не во фронте, потому что спрашивают их двое. Портал — когда рисует номер чужой записи
 * ссылкой (`apps/web/src/utils/links.ts`), и почта — когда печатает номер заявки в сводке
 * (`services/mailings/digest-waybills.ts`). Ответ обязан быть один: разойдись эти два места, и
 * ссылка из письма привела бы на список, в котором заявки нет, а карточка открылась бы поверх
 * чужих строк — или не открылась вовсе.
 *
 * Адрес адресу рознь. Заявку показывает вкладка (`tab` + `open`), рейс, список рейсов и окно
 * просмотра заявки — свои параметры (`route`, `routes`, `request`), которые читаются на любой
 * странице портала. Различие не косметическое: первая группа выбирает экран, вторая кладёт карточку
 * поверх того, где читатель уже стоит, и потому не смеет трогать чужие параметры адреса.
 *
 * Прав функции не знают и не проверяют: право — вопрос вызывающего. У портала это `can`, у письма
 * — область видимости получателя, которой заявка уже отобрана. Смешать их здесь значило бы завести
 * третий способ спрашивать разрешение.
 */

/**
 * Ключи вкладок раздела «Заказ автотехники» — те же, что читает `VehicleRequestsPage`.
 *
 * `'routes'` здесь больше нет: рейс перестал быть вкладкой и открывается окном поверх любой
 * страницы портала (`vehicleRoutePath`). Оставь ключ — и вкладка, которой нет, снова оказалась бы
 * выбираемой из почты и из ссылок портала.
 */
export type VehicleTab = 'requests' | 'on-site' | 'history' | 'archive';

/**
 * Вкладка, на которой заявку на технику показывают сейчас: пока её ведут — в списке, закрытую —
 * в журнале (ADR 0029), удалённую — в архиве (ADR 0070).
 */
export function vehicleRequestTab(status: RequestStatus, deleted = false): VehicleTab {
  if (deleted) return 'archive';
  return isClosedRequestStatus(status) ? 'history' : 'requests';
}

/**
 * Путь к карточке заявки на технику. Параметр `open` читает `useOpenedRecord` — тот же механизм,
 * которым карточку открывают ссылки внутри портала.
 */
export function vehicleRequestPath(request: {
  id: string;
  status: RequestStatus;
  deleted?: boolean;
}): string {
  return `/vehicle-requests?tab=${vehicleRequestTab(request.status, request.deleted)}&open=${request.id}`;
}

/**
 * Путь к окну просмотра заявки — тот же адрес независимо от статуса и удаления.
 *
 * Нужен там, где статуса нет структурно: строка задания путевого листа несёт один `requestId`
 * (`TaskRef` в `route-points.ts`), а `vehicleRequestPath` без статуса не выберет вкладку. Спрашивают
 * его состав рейса, задание листа, талоны журнала и занятость гаража: всем им нужна карточка поверх
 * текущего экрана, а не уход в список, где строку ещё придётся искать.
 *
 * Прав этот путь, как и всё в этом файле, не знает — и потому **из мест вызова не зовётся напрямую**:
 * право `vehicleRequests.read` спрашивает фронтовая обёртка (`apps/web/src/utils/links.ts`). Барьер
 * не формальный: механику журнал листов и гараж видны, а заявки — нет, и мимо обёртки номера стали
 * бы для него ссылками, кончающимися сообщением «Заявка не найдена или недоступна».
 */
export function vehicleRequestViewPath(requestId: string): string {
  return `/vehicle-requests?request=${requestId}`;
}

/**
 * Путь к рейсу. Рейс — окно, а не вкладка: его открывают из журнала листов, из гаража, из письма, и
 * прежний `tab=routes` уводил бы читателя с экрана, на котором он номер и увидел.
 *
 * Ключ свой (`route`), а не общий `open`, ровно поэтому: окно ложится поверх чужого адреса, а `open`
 * на фоновой странице уже занят её собственной карточкой.
 */
export function vehicleRoutePath(routeId: string): string {
  return `/vehicle-requests?route=${routeId}`;
}

/**
 * Ключи вкладок раздела «Механизация» (план `docs/mechanization-module-plan.md`, §7): заявки,
 * действующие аренды, журнал закрытых и архив.
 *
 * `rental` — не статус, а предикат Р2 («выдано и не возвращено»), и вкладкой он стал потому, что
 * это главный вопрос модуля: что мы арендуем сейчас и до какого числа.
 */
export type MechTab = 'requests' | 'rentals' | 'history' | 'archive';

/**
 * Вкладка, на которой заявку на механизацию показывают сейчас: пока её ведут — в списке, закрытую
 * — в журнале, удалённую — в архиве (ADR 0070).
 *
 * Действующая аренда ведёт в «Заявки», а не в «В аренде», и это выбор: карточку открывают, чтобы
 * прочитать заявку целиком, а вкладка присутствия отвечает на другой вопрос — «что стоит на
 * площадках», — и одной строкой её содержимое не объясняется.
 */
export function mechRequestTab(status: RequestStatus, deleted = false): MechTab {
  if (deleted) return 'archive';
  return isClosedRequestStatus(status) ? 'history' : 'requests';
}

/**
 * Путь к карточке заявки на механизацию. Параметр `open` читает `useOpenedRecord` — тот же
 * механизм, которым карточку открывают ссылки внутри портала и номера из писем.
 */
export function mechRequestPath(request: {
  id: string;
  status: RequestStatus;
  deleted?: boolean;
}): string {
  return `/mech?tab=${mechRequestTab(request.status, request.deleted)}&open=${request.id}`;
}

/**
 * Вкладка «В аренде» — выданное и не возвращённое: полный список того, что стоит на площадках.
 *
 * Ключ `rentals`, а не `rental`, и разошлись они не по небрежности: адреса заводились на Э1, когда
 * вкладки ещё не было, а имя ей дали на Э2. Пока адрес никто не спрашивал, промах был невидим —
 * ссылка молча приземлилась бы на «Заявки», то есть на список, в котором нужной строки может не
 * быть вовсе. Тип `MechTab` теперь перечисляет ровно те ключи, что стоят на странице.
 */
export const MECH_RENTAL_PATH = '/mech?tab=rentals';

/**
 * Путь к путевому листу: журнал учёта с поиском по номеру. Карточки у листа нет — журнал и есть
 * карточка: строка отвечает, на какую машину лист выписан, что с ним стало и чем подшит (ADR 0037).
 */
export function waybillPath(number: string): string {
  return `/waybills?number=${encodeURIComponent(number)}`;
}

/** Вкладка «На объекте»: техника, стоящая на площадках, — полный список строк сводки ЭСМ-2. */
export const VEHICLE_ON_SITE_PATH = '/vehicle-requests?tab=on-site';

/**
 * Полный список рейсов — куда ведёт счётчик обрезанной таблицы перевозок. Тоже окно: список и
 * карточка рейса живут одной парой, и открывать их разными способами значило бы держать два ответа
 * на «где рейсы».
 *
 * Значение у параметра фиктивное: `routes` — признак, а не идентификатор, и `1` стоит лишь затем,
 * чтобы ключ пережил разбор и сборку адреса, которые пустое значение теряют.
 */
export const VEHICLE_ROUTES_PATH = '/vehicle-requests?routes=1';

/**
 * URL parameter naming the request a copy is taken from: `?tab=requests&copy=<id>`.
 *
 * One constant for both sides. The closed-order journal builds the link and the order list reads
 * it; were the two names written separately, "Завести такую же" would land on a tab that does not
 * recognize the parameter and the copy form would silently fail to open.
 */
export const VEHICLE_REQUEST_COPY_PARAM = 'copy';

/**
 * Path to the create form prefilled from an existing order ("Завести такую же", ADR 0206): the
 * **first** tab of the section with the source named in the address.
 *
 * Its own key instead of the shared `open`, because `open` shows a card for reading while this one
 * opens the create form — one key with two meanings would diverge silently. An address rather than
 * an action in place, because the section has exactly one create form and it lives in the order
 * list: the closed-order journal shows a read-only card and carries no form of its own. Window
 * state therefore stays in the address — the link can be forwarded, and "back" leaves the form for
 * wherever the reader came from.
 */
export function vehicleRequestCopyPath(requestId: string): string {
  return `/vehicle-requests?tab=requests&${VEHICLE_REQUEST_COPY_PARAM}=${requestId}`;
}
